import { ItemTypes } from './constants';
import {
  COMPOSITION_BOX,
  GRID_SPAN,
  allCompositions,
} from '../shared/compositions/compositionCatalog';

/**
 * Dispositions de conteneur : des grilles prêtes à poser, éventuellement
 * pré-remplies. L'utilisateur en glisse une (ou la choisit depuis le « + »
 * entre deux conteneurs) et obtient d'un coup la structure qu'il aurait
 * autrement composée colonne par colonne puis ligne par ligne.
 *
 * Schéma, volontairement récursif et calqué sur le modèle de données
 * (container -> colonnes -> lignes imbriquées -> sous-colonnes, cf.
 * rowContainers dans store/containers.js) :
 *
 *   colonne = { size, box: { type } }        // feuille : une box
 *           | { size, rows: [{ columns }] }  // pile de lignes
 *
 * Une colonne porte `box` OU `rows`, jamais les deux : c'est exactement la
 * contrainte du modèle, où une colonne qui a des `rowContainerIds` ne porte
 * plus de box en direct (cf. mutateResizeBoxesUnderColumn).
 *
 * `category` :
 *  - 'grid'        : grille vide. Sans risque, se pose n'importe où, et sert
 *                    aussi de préréglage au panneau de droite pour
 *                    réorganiser un conteneur existant.
 *  - 'composition' : grille + contenu. Réservée aux dispositions dont le
 *                    contenu est évident (média + légende), et jamais
 *                    proposée pour réorganiser un conteneur déjà rempli —
 *                    elle écraserait le travail en cours. Ces entrées ne sont
 *                    pas écrites ici : elles viennent de
 *                    `src/shared/compositions/`, partagé avec l'éditeur de
 *                    site, et sont traduites en fractions + `ItemTypes`.
 *
 * Profondeur : le schéma et l'applicateur acceptent l'imbrication à
 * volonté, mais le catalogue livré s'arrête à deux niveaux. Au-delà, les
 * « Réglages avancés » du panneau de droite ne savent plus éditer la grille
 * obtenue, et l'utilisateur se retrouverait avec une structure qu'il ne peut
 * plus défaire.
 */

const half = 1 / 2;
const third = 1 / 3;
const quarter = 1 / 4;

// Raccourcis de lecture : le catalogue ci-dessous se lit comme un croquis.
const stack = (...cells) => ({
  rows: cells.map((c) => ({ columns: [{ size: 1, ...c }] })),
});

/** Vocabulaire neutre du catalogue partagé -> box du document. */
const DOCUMENT_BOX_TYPES = {
  [COMPOSITION_BOX.TEXT]: ItemTypes.TEXT,
  [COMPOSITION_BOX.TITLE_2]: ItemTypes.TITLE_2,
  [COMPOSITION_BOX.TITLE_3]: ItemTypes.TITLE_3,
  [COMPOSITION_BOX.IMAGE]: ItemTypes.IMG,
};

/** Vrai si toutes les box de la composition ont un équivalent au document. */
const documentKnowsEveryBox = ({ columns }) => {
  const known = (column) => (column.rows
    ? column.rows.every((row) => row.columns.every(known))
    : !column.box || !!DOCUMENT_BOX_TYPES[column.box]);
  return columns.every(known);
};

// Le catalogue partagé raisonne en douzièmes (la grille du site) ; le document
// en fractions. La division est exacte pour toutes les largeurs employées.
const toDocumentColumn = (column) => ({
  size: column.span / GRID_SPAN,
  ...(column.rows
    ? {
      rows: column.rows.map((row) => ({
        columns: row.columns.map(toDocumentColumn),
      })),
    }
    : {}),
  ...(column.box ? { box: { type: DOCUMENT_BOX_TYPES[column.box] } } : {}),
});

const CONTAINER_TEMPLATES = [
  // --- Grilles vides ------------------------------------------------------
  // Ces deux-là ne sont pas proposées comme blocs à poser : une colonne pleine
  // largeur ou deux moitiés se composent en un geste, la vignette prenait de la
  // place sans rien faire gagner. Elles restent au panneau de droite, où
  // « pleine largeur » est le seul moyen de ramener un conteneur à une colonne.
  {
    id: 'full',
    label: 'Pleine largeur',
    category: 'grid',
    insertable: false,
    columns: [{ size: 1 }],
  },
  {
    id: 'halves',
    label: 'Deux moitiés',
    category: 'grid',
    insertable: false,
    columns: [{ size: half }, { size: half }],
  },
  {
    id: 'third-two-thirds',
    label: 'Un tiers / deux tiers',
    category: 'grid',
    columns: [{ size: third }, { size: 2 / 3 }],
  },
  {
    id: 'thirds',
    label: 'Trois colonnes',
    category: 'grid',
    columns: [{ size: third }, { size: third }, { size: third }],
  },
  {
    id: 'quarters',
    label: 'Quatre colonnes',
    category: 'grid',
    columns: [
      { size: quarter },
      { size: quarter },
      { size: quarter },
      { size: quarter },
    ],
  },
  {
    id: 'big-then-stack',
    label: 'Grand bloc + pile',
    category: 'grid',
    columns: [{ size: half }, { size: half, ...stack({}, {}, {}) }],
  },
  {
    id: 'stack-then-big',
    label: 'Pile + grand bloc',
    category: 'grid',
    columns: [{ size: half, ...stack({}, {}, {}) }, { size: half }],
  },

  // --- Compositions pré-remplies -----------------------------------------
  // Elles viennent du catalogue partagé avec l'éditeur de site : la même
  // disposition doit porter le même nom et le même croquis des deux côtés.
  // Celles qui posent une box que le document ne connaît pas — une vidéo —
  // restent au site.
  ...allCompositions().filter(documentKnowsEveryBox).map(({ id, label, columns }) => ({
    id,
    label,
    category: 'composition',
    columns: columns.map(toDocumentColumn),
  })),
];

export const getContainerTemplate = (templateId) =>
  CONTAINER_TEMPLATES.find((tpl) => tpl.id === templateId) ?? null;

/**
 * Dispositions proposées à l'insertion (onglet Blocs, page vide). Exclut les
 * grilles marquées `insertable: false`, trop élémentaires pour valoir une
 * vignette dans un catalogue.
 */
export const insertableTemplatesByCategory = (category) =>
  CONTAINER_TEMPLATES.filter(
    (tpl) => tpl.category === category && tpl.insertable !== false
  );

/**
 * Toutes les grilles, y compris les non-insérables : le panneau de droite
 * réorganise un conteneur existant, où revenir à une colonne pleine largeur est
 * l'opération la plus courante.
 */
export const containerGridPresets = () =>
  CONTAINER_TEMPLATES.filter((tpl) => tpl.category === 'grid');

/**
 * Traduit une disposition en descripteur `[{ size, rows }]`, la forme
 * attendue par `applyContainerLayout` et par les préréglages du panneau de
 * droite. Le contenu et les sous-colonnes des lignes sont perdus au passage :
 * cette forme ne décrit qu'une grille, et n'est utilisée que pour les
 * dispositions 'grid', qui n'ont ni l'un ni l'autre.
 */
export const templateToLayout = (template) =>
  (template?.columns ?? []).map((column) => ({
    size: column.size,
    rows: column.rows?.length ?? 1,
  }));
